import React from 'react';
import { Link } from 'react-router-dom';
import { Service } from '../types';

const Home: React.FC = () => {
  const services: Service[] = [
    {
      id: 'advanced-ndt',
      name: 'Advanced NDT',
      category: 'Advanced NDT',
      description: 'Cutting-edge non-destructive testing technologies',
      features: [
        'Remote Field ECT',
        'TOFD (Time of Flight Diffraction)',
        'Phased Array Ultrasonics',
        'Ultra Wave LRT',
        'Tank Floor Scanning',
        'Eddy Current Testing',
        'Remote Visual Inspection',
        'Oxide Scale Measurement',
      ],
    },
    {
      id: 'conventional-ndt',
      name: 'Conventional NDT',
      category: 'Conventional NDT',
      description: 'Traditional and proven NDT methods',
      features: [
        'Ultrasonic Flaw Detection',
        'Thickness Gauging',
        'Magnetic Particle Testing',
        'Liquid Penetrant Testing',
        'On-Site Hardness Testing',
        'Coating Thickness Measurement',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Responsive Background */}
      <section className="relative text-white overflow-hidden">
        {/* Background: Animated on sm+ (640px+), static on mobile */}
        <div className="absolute inset-0">
          {/* Animated slideshow on sm and larger screens */}
          <div className="hidden sm:block absolute inset-0">
            {['/ndt1.jpeg', '/ndt2.jpg', '/ndt5.jpeg'].map((imgSrc, index) => (
              <div
                key={index}
                className="absolute inset-0 bg-cover bg-center opacity-0"
                style={{
                  backgroundImage: `url(${imgSrc})`,
                  animation: `fadeSlide 12s infinite ${index * 4}s`,
                }}
              />
            ))}
          </div>

          {/* Static background on mobile (xs <640px) */}
          <div
            className="absolute inset-0 sm:hidden bg-cover bg-center"
            style={{ backgroundImage: `url(/ndt1.jpeg)` }}
          />
        </div>

        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black opacity-60"></div>

        {/* Decorative animated blobs (light on mobile) */}
        <div className="absolute inset-0 opacity-5 sm:opacity-10">
          <div className="absolute top-0 left-0 w-32 h-32 sm:w-48 sm:h-48 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
          <div
            className="absolute top-0 right-0 w-32 h-32 sm:w-48 sm:h-48 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse"
            style={{ animationDelay: '2s' }}
          ></div>
          <div
            className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-32 h-32 sm:w-48 sm:h-48 bg-indigo-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse"
            style={{ animationDelay: '4s' }}
          ></div>
        </div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div className="text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight mb-4">
                Professional
                <span className="block text-blue-200 bg-gradient-to-r from-blue-200 to-blue-300 bg-clip-text text-transparent">
                  NDT Services
                </span>
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-blue-100 mb-6 leading-relaxed">
                Advanced non-destructive testing solutions with cutting-edge technology and expert engineering
                consultation for your industrial needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Link
                  to="/services"
                  className="bg-white text-avis-blue px-6 py-3 rounded-xl font-semibold text-base sm:text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 text-center hover:bg-blue-50"
                >
                  Explore Services
                </Link>
                <Link
                  to="/quotation"
                  className="border-2 border-white text-white px-6 py-3 rounded-xl font-semibold text-base sm:text-lg hover:bg-white hover:text-avis-blue transition-all duration-200 text-center shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  Request Quote
                </Link>
              </div>
            </div>
            <div className="flex justify-center mt-8 lg:mt-0">
              <div className="w-56 h-56 sm:w-64 sm:h-64 md:w-72 md:h-72 bg-white bg-opacity-10 rounded-full flex items-center justify-center backdrop-blur-md p-2 shadow-2xl border border-white/20">
                <img
                  src="/logo61.png"
                  alt="AVIS ENGINEERING & CONSULTANCY NDT SERVICES"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Our NDT Services</h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
              We provide comprehensive non-destructive testing solutions using both advanced technologies and
              conventional methods to ensure the highest quality and safety standards.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {services.map((service) => (
              <div
                key={service.id}
                className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className="mb-5">
                  <h3 className="text-xl sm:text-2xl font-bold text-avis-blue mb-2">{service.name}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </div>

                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">Available Services:</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start text-gray-700">
                        <svg
                          className="w-4 h-4 text-avis-blue mr-2 mt-0.5 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Link
                  to="/services"
                  className="inline-flex items-center text-avis-blue font-semibold hover:text-avis-dark transition-colors duration-200 text-sm"
                >
                  Learn More
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Why Choose Avis Engineering?</h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
              We combine extensive experience with cutting-edge technology to deliver reliable and accurate NDT
              solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4">
              <div className="w-14 h-14 bg-avis-blue rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Certified Expertise</h3>
              <p className="text-gray-600 text-sm">
                Our team consists of certified NDT professionals with extensive experience in various industries.
              </p>
            </div>

            <div className="text-center p-4">
              <div className="w-14 h-14 bg-avis-blue rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Advanced Technology</h3>
              <p className="text-gray-600 text-sm">
                We utilize state-of-the-art equipment to ensure accurate and reliable results.
              </p>
            </div>

            <div className="text-center p-4">
              <div className="w-14 h-14 bg-avis-blue rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Fast Turnaround</h3>
              <p className="text-gray-600 text-sm">
                We deliver quick, efficient testing services without compromising quality.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-avis-blue text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Get Started?</h2>
          <p className="text-base sm:text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
            Contact us today for a consultation or request a quote for your NDT testing requirements.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/quotation"
              className="bg-white text-avis-blue px-6 py-3 rounded-lg font-semibold text-base sm:text-lg hover:bg-blue-50 transition-colors duration-200"
            >
              Request Quote
            </Link>
            <a
              href="mailto:avis.engineering2025@gmail.com"
              className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold text-base sm:text-lg hover:bg-white hover:text-avis-blue transition-colors duration-200"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;